import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { LegendOptionsService } from './services/legend-options.service';
import { LoadingService } from './services/loading.service';
import { UtilsService } from './services/utils.service';
import { Basemaps } from './services/basemaps';
import { environment } from '../environments/environment';

import esri = __esri;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css']
})
export class AppComponent implements AfterViewInit {

    @ViewChild('applicatoinMessages') private readonly applicationMessages: ElementRef<any>;
    @ViewChild('auth') public auth: ElementRef<any>;
    @ViewChild('basemapSwitcher') private readonly basemapSwitcherRef: ElementRef<any>;
    @ViewChild('dataTable') private readonly dataTable: ElementRef<any>;
    @ViewChild('disclaimerWindow') private readonly disclaimerWindow: ElementRef<any>;
    @ViewChild('exportLayer') private readonly exportLayer: ElementRef<any>;
    @ViewChild('extent') private readonly extent: ElementRef<any>;
    @ViewChild('filterLayerOptionsWindow') private readonly filterLayerOptionsWindow: ElementRef<any>;
    @ViewChild('galleryWindow') private readonly galleryWindow: ElementRef<any>;
    @ViewChild('gallery') private readonly gallery: ElementRef<any>;
    @ViewChild('header') private readonly headerRef: any;
    @ViewChild('identify') private readonly identify: ElementRef<any>;
    @ViewChild('layerFilterContainer') private readonly layerFilterContainer: any;
    @ViewChild('layerFilterContainerWindow') private readonly layerFilterContainerWindow: ElementRef<any>;
    @ViewChild('legend') private readonly legend: any;
    @ViewChild('map') private readonly mapRef: ElementRef<any>;
    @ViewChild('spatialQuery') private readonly spatialQuery: ElementRef<any>;
    @ViewChild('spatialQueryWindow') private readonly spatialQueryWindow: ElementRef<any>;
    @ViewChild('symbolEditor') private readonly symbolEditor: ElementRef<any>;
    @ViewChild('symbolEditorWindow') private readonly symbolEditorWindow: ElementRef<any>;
    @ViewChild('tooltip') private readonly tooltip: ElementRef<any>;

    constructor(
        private legendOptionsService: LegendOptionsService,
        private loadingService: LoadingService,
        private readonly utilsService: UtilsService,
        private changeDetectorRef: ChangeDetectorRef,
        private basemaps: Basemaps
    ) { }

    dataTableMinimized: boolean = false;
    disclaimerAccepted: boolean = false;
    title = environment.pdauthAppName;
    layerIdsToBeAdded: number[] = [];
    selectedLayers: any[];
    legendClosed: boolean = false;
    loadingArea: boolean = false;
    disableMapLoader: boolean = false;
    pdauthURL: string = environment.pdauthURL;
    warnAboutZoom: boolean = true;
    currentFeature: any;
    currentDocSources: any;
    filteringLayer;

    get isCWOPAUser(): boolean {
        //this.changeDetectorRef.detectChanges();
        return (this.utilsService.userPermissions && this.utilsService.userPermissions.indexOf('View CWOPA Data') >= 0);
    }

    get isLoggedIn() {
        return this.utilsService.user;
    }

    get firstPermission(): string {
        return '';
    }

    get mapLoaderTitle() {
        return this.loadingService.keys.map(x => x).join(' , ');
    }

    get showMapLoader() {
        return this.loadingService.show;
    }

    get dataTableOpened(): string {
        return this.dataTable && this.dataTable.nativeElement && this.dataTable.nativeElement.opened;
    }

    get currentScaleIsGreaterThanMinScale(): boolean {
        if (this.utilsService.esriMap.layers && this.utilsService.esriMapView.scale) {
            const currentScale = this.utilsService.esriMapView.scale;
            let minScale = environment.minScale;
            return currentScale > minScale;
        }
        return false;
    }

    async ngAfterViewInit() {
        this.setupDisclaimer();
        this.setupAuth();
        this.changeDetectorRef.detectChanges();
        await this.mapRef.nativeElement.ready;
        await this.setBasemap();
        await this.constructExtent();
        await this.setupBasemapSwitcher();
        this.refreshLegend();
        this.setupLayerFiltering();
        this.watchMapUpdating();
        this.watchZoomLevel();
        this.watchLayersAdded();
        this.setupIdentify();
        this.setupTooltip();
    }

    async setBasemap() {
        await this.mapRef.nativeElement.ready;
        await this.mapRef.nativeElement.setBasemapsFromConfig(this.basemaps.defaultBasemap);
    }

    async constructExtent() {
        await this.extent.nativeElement.ready;
        for (let extentConfig of environment.extentConfigs) {
            if (extentConfig.label === 'AOI') {
                extentConfig.extent = async () => await this.getAoi();
            }
            this.extent.nativeElement.addExtentConfiguration(extentConfig);
        }
        this.utilsService.extent = this.extent;
    }

    async getAoi() {
        const aoiLayer = this.mapRef.nativeElement.LayerService.getAoiLayers();
        if (aoiLayer.length) {
            const aoiExtent = await this.mapRef.nativeElement.LayerService.getLayerExtent(aoiLayer[0]);
            return aoiExtent;
        }
    }

    async setupBasemapSwitcher() {
        await this.basemapSwitcherRef.nativeElement.ready;
        this.basemapSwitcherRef.nativeElement.addBasemaps(this.basemaps.basemapList, this.basemaps.basemapConfigs);
    }

    setFilters() {
        this.filterLayerOptionsWindow.nativeElement.layerId = null;
        this.filterLayerOptionsWindow.nativeElement.close();

        Promise.all(this.selectedLayers.filter(l => l.filterType === 'showAll').map(layer => {
            const layerSettings = { id: layer.id };
            return this.mapRef.nativeElement.LayerService.addMapcoreLayersToMap([layerSettings]);
        }));

        Promise.all(this.selectedLayers.filter(l => l.filterType === 'aoi').map(layer => {
            const layerSettings = { id: layer.id, query: layer.aoiQuery };
            return this.mapRef.nativeElement.LayerService.addMapcoreLayersToMap([layerSettings]);
        }));

        this.selectedLayers = this.selectedLayers.filter(l => l.filterType === 'filter');
        this.layerIdsToBeAdded = this.selectedLayers.map(l => l.id);
        this.filterLayer({});
    }

    setupAuth() {
        this.utilsService.auth = this.auth;
        this.auth.nativeElement.user.subscribe(user => {
            this.utilsService.user = user;
            this.refreshLegend();

            if (user) {
                this.auth.nativeElement.PDAuthService.getUserSettings(null, environment.pdauthAppName, user.emailAddress).subscribe(result => {
                    this.utilsService.userSettings = result.userSettings;
                    this.utilsService.setupAOISelections();
                });
            }
        });

        this.auth.nativeElement.permissions.subscribe(permissions => {
            this.utilsService.userPermissions = permissions;
            this.refreshLegend();
        });
    }

    refreshLegend() {
        if (this.legend && this.mapRef.nativeElement.LayerService.allLayers) {
            this.legend.nativeElement.refreshTree();
            this.changeDetectorRef.detectChanges();
        }
    }

    async filterLayer(event) {
        try {
            if (event.detail && event.detail.esriLayer) {
                return;
            }

            if (event.detail && event.detail.data) {
                const layerId = event.detail.id;
                if (this.layerIdsToBeAdded.indexOf(layerId) >= 0) {
                    const layerSettings = { id: layerId, query: event.detail.data.query };
                    await this.utilsService.mcMap.LayerService.addMapcoreLayersToMap([layerSettings]);
                    var index = this.layerIdsToBeAdded.indexOf(layerId);
                    if (index !== -1) this.layerIdsToBeAdded.splice(index, 1);
                    this.layerFilterContainer.nativeElement.layerId = null; //Force a reset of the layerId
                }
            }

            if (this.selectedLayers.length > 0) {
                const nextLayer = this.selectedLayers.shift();
                this.layerFilterContainer.nativeElement.layerId = null; //Force a reset of the layerId
                this.layerFilterContainer.nativeElement.layerId = nextLayer.id;
                this.layerFilterContainerWindow.nativeElement.open();
            }
        } catch (error) {
            console.error(error);
        }
    }

    setupDisclaimer() {
        const tireDisclaimerCookie = this.utilsService.getCookie('tire_disclaimer_accepted');
        this.disclaimerAccepted = tireDisclaimerCookie === 'true';
        if (!this.disclaimerAccepted) {
            this.disclaimerWindow.nativeElement.open();
            this.disclaimerWindow.nativeElement.addEventListener('openChange', open => {
                if (!open.detail) {
                    if (!this.disclaimerAccepted) {
                        this.redirectForNotAcceptingDisclaimer();
                    }
                }
            });
        } else {
            this.openAoiOrLoadDeepLink();
        }
    }

    acceptDisclaimer() {
        this.disclaimerAccepted = true;
        this.disclaimerWindow.nativeElement.close();
        this.utilsService.setCookie(environment.disclaimerCookieName, 'true', environment.disclaimerExpirationDays);
        this.openAoiOrLoadDeepLink();
    }

    async openAoiOrLoadDeepLink() {
        if (window.location.search && window.location.search.indexOf('?') >= 0) {
            //Assume deepLink
            await this.mapRef.nativeElement.ready;
            this.utilsService.loadDeepLink();
        } else {
            this.utilsService.openAoi();
        }
    }

    doNotAcceptDisclaimer() {
        this.redirectForNotAcceptingDisclaimer();
    }

    redirectForNotAcceptingDisclaimer() {
        window.location.href = environment.disclaimerDoNotAcceptUrl;
    }

    presetTmsSitesColumnSettings() {
        const columnSettings = [];
        for (const value of environment.dataTableColumnNames) {
            columnSettings.push({ name: value });
        }
        this.dataTable.nativeElement.columnSettings = columnSettings;
    }

    setupLayerFiltering() {
        window.addEventListener('addingMapcoreLayersToMap', async (event) => {
            if (event) {
                this.selectedLayers = await Promise.all((event as any).detail.map(async l => {
                    try {
                        let canFilterByAoi;
                        const aoiLayers = this.mapRef.nativeElement.LayerService.getAoiLayers();
                        if (aoiLayers.length > 0) {
                            l.aoiQuery = await this.mapRef.nativeElement.AoiService.generateFilterQuery({ parentId: l.id });
                            canFilterByAoi = !!l.aoiQuery.field;
                        }

                        const filterType = canFilterByAoi ? 'aoi' : 'filter';
                        return { ...l, ...{ filterType } };
                    } catch (error) {
                        console.error(error);
                    }
                }));
            }
            this.filterLayerOptionsWindow.nativeElement.open();
            this.filterLayerOptionsWindow.nativeElement.layerId = null;
        });
        this.layerFilterContainer.nativeElement.addEventListener('onapply', (event) => {
            this.layerFilterContainerWindow.nativeElement.close();
            this.filterLayer.bind(this);
            this.filterLayer(event);
        });
        this.layerFilterContainer.nativeElement.addEventListener('ondone', (event) => {
            this.layerFilterContainerWindow.nativeElement.close();
        });

        this.layerFilterContainer.nativeElement.addEventListener('onlayerdata', e => {
            let name = e.detail.name;
            if (this.filteringLayer && this.filteringLayer.mapcoreProps) {
                name = this.filteringLayer.mapcoreProps.name;
            }
            this.layerFilterContainerWindow.nativeElement.windowTitle = `Layer Filter: ${name}`;
        });
    }

    closeFilterLayerOptionsWindow() {
        this.filterLayerOptionsWindow.nativeElement.close();
    }

    dismissMapLoader() {
        this.loadingService.dismiss();
    }

    onSymbolEditorClick(event) {
        const esriLayer = this.utilsService.getLayerByUniqueId(event.detail.uniqueLayerId);
        this.symbolEditorWindow.nativeElement.open();
        this.symbolEditor.nativeElement.uniqueLayerId = event.detail.uniqueLayerId;
    }

    onOptionSelect(event: CustomEvent) {
        const target = event.detail.target;
        const layerId = event.detail.layerId;
        const esriLayer = this.utilsService.getLayerByUniqueId(layerId);
        const exportLayerRef = this.exportLayer.nativeElement.getComponentReference();
        const buttonClass = target.className
            .replace('dropdown-link', '')
            .replace('btn btn-transparent icon-ui-filter', '')
            .trim().toLowerCase();

        switch (buttonClass) {
            case 'copy-option':
                this.utilsService.mcMap.LayerService.copyEsriLayer(esriLayer);
                break;
            case 'export-excel-option':
                exportLayerRef.layerId = esriLayer.mapcoreProps.parentId;
                const exportExcelParams = { layerId: esriLayer.mapcoreProps.parentId, query: esriLayer.mapcoreProps.query };
                exportLayerRef.ExportService.exportToExcelAndOpenUrl(exportExcelParams);
                break;
            case 'export-shapefile-option':
                exportLayerRef.layerId = esriLayer.mapcoreProps.parentId;
                const props = esriLayer.mapcoreProps;
                const rootQuery = { operator: 'AND', subqueries: [] };
                if (props.parentQuery) {
                    rootQuery.subqueries.push(props.parentQuery);
                }

                if (props.query) {
                    rootQuery.subqueries.push(props.query);
                }

                const exportShapeFileParams = {
                    name: props.name,
                    columns: props.columns,
                    tables: props.tables,
                    query: rootQuery
                };
                exportLayerRef.ExportService.exportToShapeFileAndOpenUrl(exportShapeFileParams);
                break;
            case 'filter-option':
                this.filteringLayer = esriLayer;
                this.layerFilterContainer.nativeElement.layerId = null; //Force a reset of the layerId
                this.layerFilterContainer.nativeElement.layerId = layerId;
                this.layerFilterContainerWindow.nativeElement.open();
                break;
            case 'data-table-option':
                if (esriLayer.mapcoreProps && environment.tmsStationLayerIds.indexOf(esriLayer.mapcoreProps.parentIds) >= 0) {
                    this.presetTmsSitesColumnSettings();
                }
                this.dataTable.nativeElement.mapcoreProps = { ...esriLayer.mapcoreProps };
                this.dataTable.nativeElement.opened = true;
                break;
            case 'delete-option':
                this.mapRef.nativeElement.map.remove(esriLayer);
                break;
            case 'spatial-query-option':
                this.spatialQueryWindow.nativeElement.open();
                this.spatialQuery.nativeElement.primaryLayers = [esriLayer];
                this.spatialQuery.nativeElement.joinedLayers = this.utilsService.mapLayers.filter(l => l.id !== esriLayer.id).filter(l => l.title !== "Identify");
                this.spatialQuery.nativeElement.reset();
                break;
            case 'zoom-option':
                this.mapRef.nativeElement.zoomToLayer(layerId);
                break;
            case 'details':
                target.dispatchEvent(new CustomEvent('opendetails', { bubbles: true, cancelable: true }));
                break;
            default:
                break;
        }
        this.legendOptionsService.dropdownToggle(event.target as HTMLElement, event as any, true);
    }

    optionsAttached(event: CustomEvent<{ target: HTMLElement, layerId: string }>) {
        this.legendOptionsService.optionsAttached(event);
    }

    toggleLegend() {
        this.legendClosed = !this.legendClosed;
    }

    watchZoomLevel() {
        this.showAlertIfZoomIsTooLow();
        this.utilsService.esriMapView.watch('zoom', () => {
            this.refreshMinScaleOnLayers();
            this.showAlertIfZoomIsTooLow();
            this.changeDetectorRef.detectChanges();
        });
    }

    watchLayersAdded() {
        this.utilsService.mcMap.LayerService.mapLayersSub.subscribe(layers => {
            this.applyToolTips();
            this.refreshMinScaleOnLayers();
            this.applyCurrentYearFilters(layers);
        });
    }

    setupIdentify() {
        const loadingIdentifyKey = 'Loading Identify';
        this.utilsService.esriMapView.on('click', (event) => {
            if (this.identify.nativeElement.showIdentify) {
                this.loadingService.add(loadingIdentifyKey);
                this.utilsService.featureSelected = null;
                this.changeDetectorRef.detectChanges();
            }
        });
        this.identify.nativeElement.addEventListener('featureUpdated', (event) => {
            if (event.detail) {
                const result = event.detail;
                this.utilsService.featureSelected = JSON.parse(JSON.stringify(result));
                this.utilsService.featureSelected.attributes = result.attributes.concat([]);
                if (result.sourceLayer) {
                    this.utilsService.featureSelected.sourceLayer = result.sourceLayer;
                }
                this.loadingService.remove(loadingIdentifyKey);
            }
        });
        this.identify.nativeElement.addEventListener('status', status => {
            if (status.detail === 'loading') {
                this.loadingService.add('identify');
            } else {
                this.loadingService.remove('identify');
            }
        });
        this.utilsService.identify = this.identify;
    }

    setupTooltip() {
        this.utilsService.tooltip = this.tooltip;
    }

    refreshMinScaleOnLayers() {
        for (const layer of this.utilsService.mapLayers) {
            layer.minScale = environment.minScale;
        }
    }

    applyToolTips() {
        for (const layer of this.utilsService.mapLayers) {
            const showTooltips = layer.mapcoreProps && environment.tmsStationLayerIds.indexOf(layer.mapcoreProps.parentId) >= 0;
            if (layer.allSublayers && layer.allSublayers.items && layer.allSublayers.items.length) {
                if (layer.allSublayers.items[0].popupTemplate && !showTooltips) {
                    layer.allSublayers.items[0].popupTemplate = null;
                }
            }
        }
    }

    showAlertIfZoomIsTooLow() {
        if (this.utilsService.esriMap.layers && this.utilsService.esriMapView.scale) {
            if (this.currentScaleIsGreaterThanMinScale) {
                this.addZoomInApplicatonMessage();
            } else {
                this.warnAboutZoom = true;
                this.clearApplicationMessages();
            }
        }
    }

    watchMapUpdating() {
        this.utilsService.esriMapView.watch('updating', () => {
            const key = 'Map View Updating';
            if (this.utilsService.esriMapView.updating) {
                this.loadingService.add(key);
            } else {
                this.loadingService.remove(key);
            }
            this.changeDetectorRef.detectChanges();
        });
    }

    featureUpdated($event) {
        this.currentFeature = $event.detail;
        this.utilsService.customizeFeatureSelected(this.currentFeature);
    }

    docSourcesUpdated($event) {
        this.currentDocSources = $event.detail;
        document.body.click(); // needed to avoid change detection issue.
    }

    addZoomInApplicatonMessage() {
        setTimeout(() => {
            if (this.warnAboutZoom && this.currentScaleIsGreaterThanMinScale) {
                this.utilsService.addMessage('Zoom in to display layers');
                this.warnAboutZoom = false;
            }
        }, 100); // Timeout is required so this does not appear for newly loaded maps.
    }

    clearApplicationMessages() {
        this.applicationMessages.nativeElement.activeNotifications = [];
        setTimeout(() => {
            this.changeDetectorRef.detectChanges();
        }, 100);// Timeout is required for change detection to be applied properly
    }

    largerImageRequest($event) {
        if (this.currentDocSources) {
            this.galleryWindow.nativeElement.open();
            const imageSources = [];
            const permitNumber = this.currentFeature.attributes.filter(x => x.columnName === 'PERMIT_NUMBER')[0].value;
            let index = 0;
            for (const docSource of this.currentDocSources) {
                const docSourceFields = JSON.parse(docSource.fields);
                const featureAttribute = this.currentFeature.attributes.filter(x => x.columnName === docSourceFields.docImageField)[0];
                if (featureAttribute) {
                    imageSources.push({
                        docImageUrl: featureAttribute.value,
                        docTitle: 'Permit Number: ' + permitNumber,
                        docDesc: ""
                    });
                }
                if ($event.detail[0].docImageUrl === featureAttribute.value) {
                    this.gallery.nativeElement.currentImageIndex = index;
                }
                index++;
            }
            this.gallery.nativeElement.imageSources = imageSources;
        } else {
            this.utilsService.addMessage("Photos not found to load the gallery");
        }
    }

    applyCurrentYearFilters(layers: any) {
        if (layers.mapImage) {
            for (const layer of layers.mapImage) {
                if (layer.mapcoreProps) {
                    const layerId = layer.mapcoreProps.parentId;
                    // The custom "Current Year Traffic Count Assignment" layers are in a specified layer id range.
                    // They get a special filter based on the current year, cycle_year, and freq_cycle
                    if (layerId >= environment.currentCountAssignmentsLayerRange[0] && layerId <= environment.currentCountAssignmentsLayerRange[1]) {
                        const cycleYearValueForFreqCycle3 = this.getProperCycleYearValue(3);
                        const cycleYearValueForFreqCycle5 = this.getProperCycleYearValue(5);
                        layer.mapcoreProps.query = {
                            'operator': 'OR',
                            'subqueries': [
                                {
                                    'operator': 'AND', 'subqueries': [
                                        { 'field': 'cycle_year', 'operator': '=', 'value': '01' },
                                        { 'field': 'freq_cycle', 'operator': '=', 'value': '01' }
                                    ]
                                },
                                {
                                    'operator': 'AND', 'subqueries': [
                                        { 'field': 'cycle_year', 'operator': '=', 'value': `${cycleYearValueForFreqCycle3}` },
                                        { 'field': 'freq_cycle', 'operator': '=', 'value': '03' }
                                    ]
                                },
                                {
                                    'operator': 'AND', 'subqueries': [
                                        { 'field': 'cycle_year', 'operator': '=', 'value': `${cycleYearValueForFreqCycle5}` },
                                        { 'field': 'freq_cycle', 'operator': '=', 'value': '05' }
                                    ]
                                }
                            ]
                        }
                        this.utilsService.mcMap.LayerService.updateLayerFilter(layer, layer.mapcoreProps.query);
                    }
                }
            }
        }
    }

    getProperCycleYearValue(frequencyCycle: number) {
        let currentYear = (new Date()).getFullYear();
        // This window.year is for testing different years
        if ((<any>window).year) {
            currentYear = (<any>window).year;
        }
        const baseYear = environment.cycleBaseYear;
        const cycleYear = (currentYear - baseYear) % frequencyCycle + 1;
        return `0${cycleYear}`;
    }

    onImportShapefileDrop(event: DragEvent) {
        this.loadingService.add('shapefile');
    }

    onImportShapefileDone(event: DragEvent) {
        this.loadingService.remove('shapefile');
    }
}
